import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import usePrevious from '../../toolkit/useHooks/usePrevious';
import { AWS_BUCKET_URL } from '../../toolkit/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  addBetToBetSlip,
  removeBetFromBetSlip,
  addBetBuilderSelection,
  removeBetBuilderSelection,
  removeBetBuilderGroup,
  setBetBuilderModalShow
} from 'stores/betSlip';
import { showToaster } from 'mixin/general';
import { useTranslation } from 'react-i18next';
import useOddTransformer from '../../toolkit/useHooks/useOddsTransformer.js';
import { checkIfLiveEvent } from '../../toolkit/utils';
import { isOutcomeConflicting } from '../../toolkit/betBuilder';
import { useMediaQuery } from 'react-responsive';

function OddButton(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation('translations', { useSuspense: false });
  const betSlip = useSelector((state) => state.betSlip);
  const betBuilder = useSelector((state) => state.betSlip.betBuilder);
  const { providerId, marketStringId, outcomeId } = props;
  const match = useSelector((state) => state.match.events[providerId]);
  const betBuilderOfferedMarkets = useSelector(
    (state) => state.match.betBuilderAvailableSelections?.[providerId]?.data?.markets
  );
  const [selected, setSelected] = useState(false);
  const [betBuilderSelected, setBetBuilderSelected] = useState(false);
  const [oddsClass, setOddsClass] = useState('');
  // Market/outcome are derived during render (not stored via an effect) so the
  // very first paint already has the odds. Deriving them in a useEffect made the
  // first rendered frame use an empty outcome, which printed NaN until some
  // other interaction re-rendered the button.
  const { market, outcome, renderNull } = useMemo(() => {
    const foundMarket = match?.markets?.find((m) => m.stringId === marketStringId);
    const foundOutcome = foundMarket?.outcomes?.find((o) => o.id === outcomeId);
    return {
      market: foundMarket ?? {},
      outcome: foundOutcome,
      renderNull: !foundMarket || !foundOutcome
    };
  }, [match, marketStringId, outcomeId]);
  const prevOdds = usePrevious(outcome?.odds);
  const updateTimer = useRef(null);
  const hasOdds = typeof outcome?.odds === 'number' && outcome.odds !== 0;
  const oddsTransformer = useOddTransformer();
  const isDesktop = useMediaQuery({
    query: '(min-width: 992px)'
  });

  // Find if this outcome is in the betslip as part of a bet builder group (for remove-on-click)
  const betBuilderBetInSlip = betSlip.bets.find(
    (b) =>
      b.providerId === providerId &&
      b.marketStringId === marketStringId &&
      b.outcomeId === outcomeId &&
      b.isBetBuilder === true
  );

  // In bet builder mode MyOdds tells which further outcomes would make the selection impossible
  const isBetBuilderConflict =
    betBuilder.isEnabled &&
    !betBuilderSelected &&
    betBuilder.selections[0]?.providerId === providerId &&
    isOutcomeConflicting(
      betBuilder.conflicts,
      market.id,
      marketStringId,
      outcomeId,
      betBuilderOfferedMarkets
    );

  const actionBetSlipOdds = useCallback((e) => {
    e.preventDefault();

    // Check if bet builder mode is enabled
    if (betBuilder.isEnabled) {
      // Bet Builder mode - add/remove from bet builder selections or remove group from betslip
      if (isBetBuilderConflict) {
        showToaster(t('betBuilder.conflictingSelection'), 'error');
        return;
      }
      if (betBuilderSelected) {
        // In pending selections: remove from bet builder selections
        dispatch(
          removeBetBuilderSelection({
            providerId: providerId,
            marketStringId: marketStringId
          })
        );
      } else if (betBuilderBetInSlip?.betBuilderId !== undefined) {
        // In betslip as bet builder: remove the entire bet builder group
        dispatch(removeBetBuilderGroup({ betBuilderId: betBuilderBetInSlip.betBuilderId }));
      } else {
        // Not selected: add to bet builder selections
        const hasNormalBet = betSlip.bets.some(
          (b) => b.providerId === providerId && !b.isBetBuilder
        );
        if (hasNormalBet) {
          showToaster('If custom bet is added for sport event, you cannot add normal market', 'error');
          return;
        }
        dispatch(
          addBetBuilderSelection({
            providerId: providerId,
            marketStringId: marketStringId,
            marketId: market.id,
            outcomeId: outcomeId,
            outcomeName: outcome?.name,
            odds: outcome?.odds,
            match: match
          })
        );
        dispatch(setBetBuilderModalShow(true));
      }
    } else {
      // Normal mode - add/remove from betslip
      if (betBuilderBetInSlip?.betBuilderId !== undefined) {
        // In betslip as bet builder: remove the entire bet builder group when clicking
        dispatch(removeBetBuilderGroup({ betBuilderId: betBuilderBetInSlip.betBuilderId }));
      } else if (selected) {
        dispatch(
          removeBetFromBetSlip({
            providerId: providerId,
            marketStringId: marketStringId,
            outcomeId: outcomeId
          })
        );
      } else {
        const hasBetBuilderBet = betSlip.bets.some(
          (b) => b.providerId === providerId && b.isBetBuilder === true
        );
        if (hasBetBuilderBet) {
          showToaster('If custom bet is added for sport event, you cannot add normal market', 'error');
          return;
        }
        dispatch(
          addBetToBetSlip({
            providerId: providerId,
            marketStringId: marketStringId,
            marketId: market.id,
            outcomeId: outcomeId,
            outcomeName: outcome?.name,
            odds: outcome?.odds,
            match: match
          })
        );
      }
    }
  }, [
    betBuilder.isEnabled,
    isBetBuilderConflict,
    betBuilderSelected,
    selected,
    betBuilderBetInSlip,
    providerId,
    marketStringId,
    outcomeId,
    market?.id,
    outcome?.name,
    outcome?.odds,
    match,
    betSlip.bets,
    dispatch
  ]);

  useEffect(() => {
    // Check normal betslip selection status
    // Exclude bet builder bets from showing as selected
    const selectionStatus = betSlip.bets.some(
      (b) =>
        b.providerId === providerId &&
        b.marketStringId === marketStringId &&
        b.outcomeId === outcomeId &&
        !b.isBetBuilder
    );
    if (selected !== selectionStatus) {
      setSelected(selectionStatus);
    }
  }, [betSlip.bets, providerId, marketStringId, outcomeId]);

  useEffect(() => {
    // Check bet builder selection status
    const betBuilderSelectionStatus = betBuilder.selections.some(
      (s) =>
        s.providerId === providerId &&
        s.marketStringId === marketStringId &&
        s.outcomeId === outcomeId
    );
    if (betBuilderSelected !== betBuilderSelectionStatus) {
      setBetBuilderSelected(betBuilderSelectionStatus);
    }
  }, [betBuilder.selections, providerId, marketStringId, outcomeId]);

  //   const usePreviousValue = (value) => {
  //     const ref = useRef();
  //     useEffect(() => {
  //       ref.current = value;
  //     });
  //     return ref.current;
  //   };

  function setUpdate(oddsClass) {
    updateTimer.current = setTimeout(() => {
      setOddsClass(oddsClass);
      updateTimer.current = null;
    }, 5000);
  }

  useEffect(() => {
    const newOdds = outcome?.odds;
    if (prevOdds && newOdds !== prevOdds) {
      setOddsClass(` odd-${newOdds > prevOdds ? 'increased' : 'decreased'}`);
      setUpdate('');
    }
  }, [outcome?.odds, prevOdds]);

  useEffect(() => {
    return () => {
      if (updateTimer.current) {
        clearTimeout(updateTimer.current);
        updateTimer.current = null;
      }
    };
  }, []);

  if (renderNull || outcome?.odds == 1.0) {
    if (isDesktop) {
      return (
        <div className="border-tertiary rounded justify-content-center text-dark px-1 d-inline-flex w-100 fw-bold mt-auto pt-1 pb-1 fs-8">
          <img
            className="icon-white"
            width="17"
            height="18"
            src={`${AWS_BUCKET_URL}/Icons/lockicon.svg`}
          />
        </div>
      );
    } else {
      return (
        <div className="border-tertiary rounded justify-content-center text-dark px-1 d-inline-flex w-100 fw-bold mt-auto pt-1 pb-1 fs-8">
          <img width="17" height="18" src={`${AWS_BUCKET_URL}/Icons/lockicon.svg`} />
        </div>
      );
    }
  }

  // Show as selected: normal bet, pending bet builder selection, or in betslip as bet builder
  const isSelected = selected || betBuilderSelected || !!betBuilderBetInSlip;

  return (
    <div
      key={outcomeId}
      className={
        (isBetBuilderConflict ? 'betOddsBox-conflict ' : '') +
        (isDesktop
          ? isSelected
            ? `rounded px-2 d-flex w-100 fw-bold mt-auto pt-1 pb-1 fs-6 text-dark ${props.inlineTitle ? 'justify-content-between' : 'justify-content-center'}` +
            oddsClass
            : `rounded text-white px-2 d-flex w-100 fw-bold mt-auto pt-1 pb-1 fs-6 ${props.inlineTitle ? 'justify-content-between' : 'justify-content-center'}` +
            oddsClass
          : `border-tertiary rounded text-dark px-1 d-flex w-100 fw-bold mt-auto pt-1 pb-1 fs-8 ${props.inlineTitle ? 'justify-content-between' : 'justify-content-center'}` +
          oddsClass)
      }
      style={{
        backgroundColor: isDesktop
          ? isSelected
            ? hasOdds
              ? '#FFCC00'  // Yellow for all selected states (both normal and bet builder)
              : '#696767'
            : '#696767'
          : isSelected
            ? hasOdds
              ? '#FFCC00'  // Yellow for all selected states (both normal and bet builder)
              : '#696767'
            : 'transparent'
      }}
      onClick={actionBetSlipOdds}>
      {props.inlineTitle && (
        <span className="me-1">{props.inlineTitle}</span>
      )}
      <span>
        {hasOdds
          ? oddsTransformer.transformWithDisplayFormatted(
            checkIfLiveEvent(match),
            match.isSeason,
            market.id,
            new Date(match.gameDate),
            outcome.odds,
            marketStringId
          )
          : '-'}
      </span>
    </div>
  );
}
export default OddButton;
